export interface FrontendGeoHint {
  country: string;
  countryCode: string;
  region: string;
  regionCode: string;
  city: string;
}

export interface SubmissionFormDescriptor {
  hasHoneypot: boolean;
  action: string;
  hxPost: string;
}

export interface SubmissionGeoRuntimeConfig {
  submissionEndpoints: string[];
  geoHintUrl: string;
  geoHintTimeoutMs: number;
}

const GEO_COUNTRY_CODE_FIELD = '_zeroy_geo_country_code';
const GEO_REPLAY_FLAG = 'zeroyGeoReplay';
const TRAILING_SLASHES_RE = /\/+$/;
const COUNTRY_CODE_RE = /^[A-Z]{2}$/;

async function fetchGeoHint(
  url: string,
  timeoutMs: number
): Promise<FrontendGeoHint | null> {
  let timeoutId = 0;

  try {
    const controller = new AbortController();
    const safeTimeoutMs = Math.max(0, timeoutMs || 0);

    if (safeTimeoutMs > 0) {
      timeoutId = window.setTimeout(() => controller.abort(), safeTimeoutMs);
    }

    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      return null;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('application/json')) {
      return null;
    }

    return normalizeGeoHintResponse(await response.json());
  } catch {
    return null;
  } finally {
    if (timeoutId > 0) {
      window.clearTimeout(timeoutId);
    }
  }
}

function normalizeRestRoute(route: string): string {
  const trimmed = route.trim().replace(TRAILING_SLASHES_RE, '');
  if (!trimmed) {
    return '';
  }

  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

export function buildSubmissionEndpointSignature(
  value: string,
  baseUrl: string
): string | null {
  if (!value.trim()) {
    return null;
  }

  try {
    const url = new URL(value, baseUrl);
    const restRoute = normalizeRestRoute(
      url.searchParams.get('rest_route') ?? ''
    );
    if (restRoute) {
      return `rest:${restRoute}`;
    }

    const marker = '/wp-json/';
    const markerIndex = url.pathname.indexOf(marker);
    if (markerIndex >= 0) {
      const route = normalizeRestRoute(
        url.pathname.slice(markerIndex + marker.length)
      );
      if (route) {
        return `rest:${route}`;
      }
    }

    const pathname = url.pathname.replace(TRAILING_SLASHES_RE, '');
    if (!pathname) {
      return null;
    }

    return `${url.origin}${pathname}`;
  } catch {
    return null;
  }
}

function ensureHiddenInput(
  form: HTMLFormElement,
  name: string
): HTMLInputElement {
  const existing = form.querySelector(`input[name="${name}"]`);

  if (existing instanceof HTMLInputElement) {
    existing.type = 'hidden';
    return existing;
  }

  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  form.append(input);
  return input;
}

export function isZeroYSubmissionTarget(
  descriptor: SubmissionFormDescriptor,
  submissionEndpoints: string[],
  baseUrl = window.location.href
): boolean {
  if (descriptor.hasHoneypot) {
    return true;
  }

  const endpointSignatures = new Set(
    submissionEndpoints
      .map((value) => buildSubmissionEndpointSignature(value, baseUrl))
      .filter((value): value is string => Boolean(value))
  );

  const actionCandidates = [descriptor.hxPost, descriptor.action];

  return actionCandidates.some((value) => {
    const signature = buildSubmissionEndpointSignature(value, baseUrl);
    return signature !== null && endpointSignatures.has(signature);
  });
}

export function isZeroYSubmissionForm(
  form: HTMLFormElement,
  submissionEndpoints: string[],
  baseUrl = window.location.href
): boolean {
  return isZeroYSubmissionTarget(
    {
      hasHoneypot: Boolean(form.querySelector('input[name="_zeroy_hp"]')),
      action: form.getAttribute('action') ?? '',
      hxPost: form.getAttribute('hx-post') ?? '',
    },
    submissionEndpoints,
    baseUrl
  );
}

export function buildGeoHintPrivateFields(
  hint: FrontendGeoHint
): Record<string, string> {
  return {
    _zeroy_geo_city: hint.city,
    [GEO_COUNTRY_CODE_FIELD]: hint.countryCode,
    _zeroy_geo_region: hint.region,
    _zeroy_geo_region_code: hint.regionCode,
  };
}

export function setGeoHintFields(
  form: HTMLFormElement,
  hint: FrontendGeoHint
): void {
  for (const [name, value] of Object.entries(buildGeoHintPrivateFields(hint))) {
    ensureHiddenInput(form, name).value = value;
  }
}

export function normalizeGeoHintResponse(
  payload: unknown
): FrontendGeoHint | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const country =
    'country_name' in payload && typeof payload.country_name === 'string'
      ? payload.country_name.trim()
      : '';
  const countryCode =
    'country_code' in payload && typeof payload.country_code === 'string'
      ? payload.country_code.trim().toUpperCase()
      : '';
  const region =
    'region' in payload && typeof payload.region === 'string'
      ? payload.region.trim()
      : '';
  const regionCode =
    'region_code' in payload && typeof payload.region_code === 'string'
      ? payload.region_code.trim().toUpperCase()
      : '';
  const city =
    'city' in payload && typeof payload.city === 'string'
      ? payload.city.trim()
      : '';

  if (!COUNTRY_CODE_RE.test(countryCode)) {
    return null;
  }

  return {
    country,
    countryCode,
    region,
    regionCode,
    city,
  };
}

function replayFormSubmit(
  form: HTMLFormElement,
  submitter: HTMLElement | null
): void {
  form.dataset[GEO_REPLAY_FLAG] = '1';

  if (typeof form.requestSubmit === 'function') {
    if (
      submitter instanceof HTMLButtonElement ||
      submitter instanceof HTMLInputElement
    ) {
      form.requestSubmit(submitter);
      return;
    }

    form.requestSubmit();
    return;
  }

  form.submit();
}

export function initSubmissionGeoHinting(
  config: SubmissionGeoRuntimeConfig
): void {
  const trackedForms = new Set<HTMLFormElement>();
  const endpointSignatures = new Set(
    config.submissionEndpoints
      .map((value) =>
        buildSubmissionEndpointSignature(value, window.location.href)
      )
      .filter((value): value is string => Boolean(value))
  );

  let geoHintPromise: Promise<FrontendGeoHint | null> | null = null;
  let geoHintResult: FrontendGeoHint | null | undefined;

  const registerForm = (form: HTMLFormElement) => {
    if (trackedForms.has(form)) {
      if (geoHintResult) {
        setGeoHintFields(form, geoHintResult);
      }
      return;
    }

    if (
      !isZeroYSubmissionForm(
        form,
        [...endpointSignatures],
        window.location.href
      )
    ) {
      return;
    }

    trackedForms.add(form);
    ensureHiddenInput(form, GEO_COUNTRY_CODE_FIELD);
    ensureHiddenInput(form, '_zeroy_geo_region');
    ensureHiddenInput(form, '_zeroy_geo_region_code');
    ensureHiddenInput(form, '_zeroy_geo_city');

    if (geoHintResult) {
      setGeoHintFields(form, geoHintResult);
    }
  };

  const registerRoot = (root: ParentNode | Node) => {
    if (root instanceof HTMLFormElement) {
      registerForm(root);
      return;
    }

    if (!(root instanceof Element || root instanceof Document)) {
      return;
    }

    for (const form of root.querySelectorAll('form')) {
      if (form instanceof HTMLFormElement) {
        registerForm(form);
      }
    }
  };

  const applyHintToTrackedForms = (hint: FrontendGeoHint | null) => {
    if (!hint) {
      return;
    }

    for (const form of trackedForms) {
      setGeoHintFields(form, hint);
    }
  };

  const loadGeoHint = (): Promise<FrontendGeoHint | null> => {
    if (geoHintResult !== undefined) {
      return Promise.resolve(geoHintResult);
    }

    if (!config.geoHintUrl.trim()) {
      geoHintResult = null;
      return Promise.resolve(null);
    }

    if (geoHintPromise) {
      return geoHintPromise;
    }

    geoHintPromise = (async () => {
      try {
        geoHintResult = await fetchGeoHint(
          config.geoHintUrl,
          config.geoHintTimeoutMs
        );
        applyHintToTrackedForms(geoHintResult);
        return geoHintResult;
      } catch {
        geoHintResult = null;
        return null;
      } finally {
        geoHintPromise = null;
      }
    })();

    return geoHintPromise;
  };

  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const form = target.closest('form');
      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      registerForm(form);

      if (trackedForms.has(form)) {
        loadGeoHint();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      registerForm(form);
      if (!trackedForms.has(form)) {
        return;
      }

      if (form.dataset[GEO_REPLAY_FLAG] === '1') {
        delete form.dataset[GEO_REPLAY_FLAG];
        return;
      }

      if (geoHintResult !== undefined) {
        if (geoHintResult) {
          setGeoHintFields(form, geoHintResult);
        }
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitter = event instanceof SubmitEvent ? event.submitter : null;

      loadGeoHint().then((hint) => {
        if (hint) {
          setGeoHintFields(form, hint);
        }

        replayFormSubmit(form, submitter);
      });
    },
    true
  );

  registerRoot(document);

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        registerRoot(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}
